'use client';

import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { AvisoDatasNoVesto } from '@/app/ops/components/AvisoDatasNoVesto';
import { useConfiguracao } from '@/domain/configuracao/hooks/useConfiguracao';
import { useFila } from '@/domain/fila/hooks/useFila';
import { useHome } from '@/domain/home/hooks/useHome';
import { useImplantacao } from '@/domain/implantacao/hooks/useImplantacao';
import { useExportacaoVesto } from '@/domain/ops/hooks/useExportacaoVesto';
import { useOps } from '@/domain/ops/hooks/useOps';
import { PERMISSION } from '@/domain/permissions/constants/permissionIds';
import { usePermissions } from '@/domain/permissions/hooks/usePermissions';
import { cn } from '@/lib/utils';

import { HomeCarteiraVelha } from './HomeCarteiraVelha';
import { HomeFilaPreview } from './HomeFilaPreview';
import { HomeFontesCard } from './HomeFontesCard';
import { HomeImplantacaoLinha } from './HomeImplantacaoLinha';
import { HomeMetrics } from './HomeMetrics';
import { HomeMudancasHoje } from './HomeMudancasHoje';

/** A Carteira é a tela dona do arquivo: o aviso do Início leva para lá, não exporta daqui. */
const CARTEIRA_PATH = '/ops';

/**
 * The "Hoje" tab: the day's guide, exactly as the Início screen always showed it.
 *
 * It lives in its own component because Início now has two tabs, and the executive view is
 * the other one. The queue is NOT loaded here: the Copiloto sits in the page title, which
 * both tabs share, so the page owns that load and this panel reads the same store.
 */
export function HomeHojePanel() {
  const { t } = useTranslation('common');
  /**
   * The day's guide reads three areas: the portfolio (numbers, its age, the VESTO notice and the
   * sources), the Fila (its preview and what changed today) and Settings (the implantation
   * line). Each block appears, and loads, only for a role that reads its area: the API would
   * refuse the rest.
   */
  const { can } = usePermissions();
  const veCarteira = can(PERMISSION.CARTEIRA_VIEW);
  const veFila = can(PERMISSION.FILA_VIEW) || can(PERMISSION.MATERIAIS_VIEW);
  const veImplantacao = can(PERMISSION.CONFIGURACOES_VIEW);
  const exportaVesto = can(PERMISSION.CARTEIRA_EDIT);
  const { resumo, resumoLoading, resumoFailed, loadResumo } = useHome();
  const {
    fila,
    loading: filaLoading,
    failed: filaFailed,
    loadFila,
    mudancasHoje,
    mudancasLoading,
    mudancasFailed,
    loadMudancasHoje,
  } = useFila();
  // The parameters the explanations quote (tolerated deviation, minimum sample) are read
  // from Settings, never written into the text.
  const { config, load: loadConfig } = useConfiguracao();
  const { pendencias, loading: pendenciasLoading, failed: pendenciasFailed, loadPendencias } = useImplantacao();
  /** A idade da carteira: toda tela lê o último arquivo, e nenhuma dizia há quanto tempo. */
  const { ultimaImportacao, ultimaImportacaoLoading, ultimaImportacaoFailed, loadUltimaImportacao } = useOps();

  /**
   * Quantas OPs estão com data diferente da que foi para o VESTO. O Início só AVISA: quem
   * exporta é a Carteira, que é a tela dona do arquivo, então o botão daqui leva para lá em
   * vez de gerar o arquivo de dentro do painel do dia.
   */
  const { pendencia: pendenciaVesto, carregarPendencia } = useExportacaoVesto();
  const router = useRouter();

  const irParaACarteira = useCallback((): void => {
    router.push(CARTEIRA_PATH);
  }, [router]);

  useEffect(() => {
    const run = async (): Promise<void> => {
      await Promise.all([
        veCarteira ? loadResumo() : Promise.resolve(),
        loadConfig(),
        veFila ? loadMudancasHoje() : Promise.resolve(),
        veImplantacao ? loadPendencias() : Promise.resolve(),
        veCarteira ? loadUltimaImportacao() : Promise.resolve(),
        veCarteira ? carregarPendencia() : Promise.resolve(),
      ]);
    };
    run();
  }, [
    carregarPendencia,
    loadConfig,
    loadMudancasHoje,
    loadPendencias,
    loadResumo,
    loadUltimaImportacao,
    veCarteira,
    veFila,
    veImplantacao,
  ]);

  const handleRetryResumo = useCallback((): void => {
    const run = async (): Promise<void> => {
      await loadResumo();
    };
    run();
  }, [loadResumo]);

  const handleRetryMudancas = useCallback((): void => {
    const run = async (): Promise<void> => {
      await loadMudancasHoje();
    };
    run();
  }, [loadMudancasHoje]);

  const handleRetryFila = useCallback((): void => {
    const run = async (): Promise<void> => {
      await loadFila();
    };
    run();
  }, [loadFila]);

  // The numbers come from both sources, so their retry asks both again.
  const handleRetryNumeros = useCallback((): void => {
    const run = async (): Promise<void> => {
      await Promise.all([loadResumo(), veFila ? loadFila() : Promise.resolve()]);
    };
    run();
  }, [loadFila, loadResumo, veFila]);

  // Nothing loaded yet and no failure recorded means the first load is still on its way.
  const resumoCarregando = resumoLoading || (resumo === null && !resumoFailed);
  const filaCarregando = veFila && (filaLoading || (fila === null && !filaFailed));
  const mudancasCarregando = mudancasLoading || (mudancasHoje === null && !mudancasFailed);
  const pendenciasCarregando = pendenciasLoading || (pendencias === null && !pendenciasFailed);

  if (!veCarteira && !veFila) {
    return (
      <NoDataFound
        variant="permission"
        size="md"
        title={t('home.semDadosDoPapel.titulo')}
        description={t('home.semDadosDoPapel.descricao')}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-y-5">
      {veCarteira && (
        <HomeMetrics
          resumo={resumo}
          desvios={fila?.contagem?.DESVIO ?? null}
          loading={resumoCarregando || filaCarregando}
          resumoFailed={resumoFailed}
          filaFailed={filaFailed}
          onRetry={handleRetryNumeros}
          tolerancia={config?.prazo?.desvioTolerancia ?? null}
          amostraMinima={config?.apontamento?.apontMinAmostra ?? null}
        />
      )}

      {/* Logo abaixo dos números: se a carteira é de uma semana atrás, isso muda como ler
          TODOS eles, e dizer no rodapé seria dizer tarde. */}
      {veCarteira && (
        <HomeCarteiraVelha
          ultimaEm={ultimaImportacao?.createdAt ?? null}
          avisarComMaisDeDias={config?.importacao?.avisarComMaisDeDias}
          silenciar={ultimaImportacaoLoading || ultimaImportacaoFailed}
        />
      )}

      {/* O outro lado da mesma porta: a carteira entrou hoje, e as datas que o motor
          recalculou ainda não voltaram. SOME sozinho quando não há diferença. */}
      {veCarteira && pendenciaVesto.comDataDiferente > 0 && (
        <AvisoDatasNoVesto
          comDataDiferente={pendenciaVesto.comDataDiferente}
          exportando={false}
          onExportar={exportaVesto ? irParaACarteira : undefined}
        />
      )}

      {/* Uma LINHA, nao o roteiro inteiro: a lista item por item mora em Configuracoes >
          Implantacao, que e a tela dona do assunto. Aqui fica o tamanho e o caminho, e
          so enquanto houver o que validar. */}
      {veImplantacao && (
        <HomeImplantacaoLinha total={pendencias?.total ?? 0} silenciar={pendenciasCarregando || pendenciasFailed} />
      )}

      {/* Side by side from laptop up: the day's list leads and the sources read beside it,
          both stretched to the same height. Below that they stack, in the same order. */}
      <div className="flex flex-col gap-4 laptop:flex-row">
        {veFila && (
          <HomeFilaPreview
            className={cn('min-w-0', veCarteira ? 'laptop:basis-[calc(60%_-_0.5rem)]' : 'basis-full')}
            itens={fila?.itens ?? []}
            loading={filaCarregando}
            failed={filaFailed}
            onRetry={handleRetryFila}
          />
        )}

        {veCarteira && (
          <HomeFontesCard
            className={cn('min-w-0', veFila ? 'laptop:basis-[calc(40%_-_0.5rem)]' : 'basis-full')}
            fontes={resumo?.fontes ?? []}
            loading={resumoCarregando}
            failed={resumoFailed}
            onRetry={handleRetryResumo}
          />
        )}
      </div>

      {/* Comunicacao do dia, compacta e abaixo da lista do dia: o que ja mudou sozinho. */}
      {veFila && (
        <HomeMudancasHoje
          mudancas={mudancasHoje ?? []}
          loading={mudancasCarregando}
          failed={mudancasFailed}
          onRetry={handleRetryMudancas}
        />
      )}
    </div>
  );
}
